import Phaser from "phaser";

/**
 * The static legal pages this footer links to - plain HTML files under
 * `public/` (see `public/privacy.html`/`terms.html`/`contact.html`),
 * served as-is by Vite/Netlify outside the Phaser bundle. This app has no
 * client-side router (see `daily/challengeRoute.ts`'s own doc - the `/c/
 * <id>` Beat My Score route is the entire routing surface it adds), so a
 * plain static page is the smallest way to add launch-required legal
 * pages without touching game/scene navigation at all.
 *
 * Paths are resolved against Vite's own `import.meta.env.BASE_URL` (the
 * configured `base`, always trailing-slash-terminated) rather than
 * hardcoded absolute paths: "/" in the normal snakescore.com build (so
 * these still open exactly "/privacy.html" etc., unchanged), "./" in the
 * CrazyGames distribution build (see `vite.config.crazygames.ts`), so
 * they open relative to wherever the game bundle is actually hosted
 * instead of the domain root.
 */
const BASE_URL = import.meta.env.BASE_URL;
const LEGAL_LINKS: ReadonlyArray<{ label: string; path: string }> = [
  { label: "Privacy", path: `${BASE_URL}privacy.html` },
  { label: "Terms", path: `${BASE_URL}terms.html` },
  { label: "Contact", path: `${BASE_URL}contact.html` },
];

export interface LegalFooterHandle {
  container: Phaser.GameObjects.Container;
  relayout(): void;
  destroy(): void;
}

/**
 * A tiny, discreet "Privacy · Terms · Contact" footer - launch requirement
 * (PR33) to make the legal pages reachable from the public site without
 * touching Instant Play, gameplay, mobile controls, or the first-run
 * flow. Anchored to the very bottom edge, in the same muted secondary-text
 * gray this codebase already uses for fine print (see
 * `DataPrivacyModal`'s closing line), well below every primary
 * button/tap target on both `EntryScene` and `MenuScene`. Opens each page
 * in a new tab (`window.open(..., "_blank")`) rather than navigating this
 * one away, so a tap never loses in-progress game state.
 */
export function createLegalFooter(scene: Phaser.Scene, depth = 0): LegalFooterHandle {
  const container = scene.add.container(0, 0).setDepth(depth);

  const texts = LEGAL_LINKS.map(({ label, path }) => {
    const text = scene.add
      .text(0, 0, label, {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        color: "#5b6178",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    text.on("pointerover", () => text.setColor("#8b93a7"));
    text.on("pointerout", () => text.setColor("#5b6178"));
    text.on("pointerdown", () => {
      // Same `globalThis.window?...?.()` defensive-optional-chaining
      // convention `NameEntryModal.ts`/`ShareChallengeModal.ts` already use
      // for a browser-only global - a safe no-op wherever `window` doesn't
      // exist (e.g. this project's own `vitest run` node environment),
      // never a thrown ReferenceError.
      globalThis.window?.open?.(path, "_blank", "noopener");
    });
    container.add(text);
    return text;
  });

  const GAP = 18;

  function relayout(): void {
    const { width, height } = scene.scale;
    const totalWidth = texts.reduce((sum, text) => sum + text.width, 0) + GAP * (texts.length - 1);
    let x = width / 2 - totalWidth / 2;
    const y = height - 18;
    for (const text of texts) {
      text.setPosition(x + text.width / 2, y);
      x += text.width + GAP;
    }
  }

  relayout();

  return {
    container,
    relayout,
    destroy() {
      container.destroy();
    },
  };
}
